import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { MaterialSymbol } from '../icons/MaterialSymbol';

interface LibraryFormSheetProps {
  visible: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}

export function LibraryFormSheet({
  visible,
  title,
  onClose,
  children,
  footer,
}: LibraryFormSheetProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    if (!visible) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [visible, onClose]);

  if (!visible || !mounted || typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: 'rgba(0, 0, 0, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
        fontFamily:
          "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
      }}
    >
      <section
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          width: '100%',
          maxWidth: 560,
          maxHeight: 'calc(100vh - 80px)',
          backgroundColor: 'var(--menu-bg)',
          boxShadow: 'var(--gem-dialog-shadow)',
          borderRadius: 28,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          color: 'var(--text-primary)',
        }}
      >
        {/* Header: Gemini-style 3-column grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '40px 1fr 40px',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid var(--input-border)',
            columnGap: 8,
            flexShrink: 0,
          }}
        >
          <div />
          <h2
            style={{
              fontFamily: "'Google Sans', sans-serif",
              fontSize: 22,
              fontWeight: 500,
              lineHeight: '28px',
              color: 'var(--text-primary)',
              margin: 0,
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: 0,
              background: 'transparent',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 150ms',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--hover-strong)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <MaterialSymbol name="close" size={20} />
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '20px 24px',
          }}
        >
          {children}
        </div>

        {footer ? (
          <div
            style={{
              borderTop: '1px solid var(--input-border)',
              padding: '14px 20px',
              flexShrink: 0,
            }}
          >
            {footer}
          </div>
        ) : null}
      </section>
    </div>,
    document.body,
  );
}
